import clsx from 'clsx'
import React, { forwardRef } from 'react'
import type {
  AriaAttributes,
  KeyboardEventHandler,
  HTMLAttributeAnchorTarget,
  MouseEventHandler,
  Ref,
  ReactNode,
} from 'react'
import type SvgIcon from '../SvgIcon/SvgIcon'
import { toDataSet } from '../utilities/toDataSet'

export interface ButtonProps {
  'aria-controls'?: AriaAttributes['aria-controls']
  'aria-expanded'?: AriaAttributes['aria-expanded']
  'aria-haspopup'?: AriaAttributes['aria-haspopup']
  children?: ReactNode & { type?: { uiName?: string } }
  color?: ButtonColor
  dataSet?: Record<string, string | undefined>
  disabled?: boolean
  endAdornment?: typeof SvgIcon
  form?: string
  href?: string
  id?: string
  onClick?: MouseEventHandler<HTMLElement>
  onKeyDown?: KeyboardEventHandler<HTMLElement>
  position?: ButtonPosition | null
  size?: ButtonSize
  startAdornment?: typeof SvgIcon
  tabIndex?: number
  target?: HTMLAttributeAnchorTarget
  type?: ButtonType
  variant?: ButtonVariant
}

export type ButtonColor =
  | 'danger'
  | 'default'
  | 'inverse'
  | 'primary'
  | 'secondary'

type ButtonColors = {
  [key in ButtonColor]: string
}

type ButtonPosition = 'left' | 'middle' | 'right'

type ButtonPositions = {
  [key in ButtonVariant]: {
    [key in ButtonColor]: { [key in ButtonPosition]: string }
  }
}

export type ButtonSize = 'large' | 'medium' | 'small'

type ButtonSizes = {
  [key in ButtonSize]: {
    root: string
    icon: string
  }
}

type ButtonType = 'button' | 'reset' | 'submit'

export type ButtonVariant = 'contained' | 'outlined' | 'text'

type ButtonVariants = {
  [key in ButtonVariant]: ButtonColors
}

type ButtonDisabledVariants = {
  [key in ButtonVariant]: {
    default: string
    inverse: string
  }
}

const sizes: ButtonSizes = {
  large: {
    root: 'h-12 px-4 py-3',
    icon: 'w-6 h-6',
  },
  medium: {
    root: 'h-10 px-2 py-2',
    icon: 'w-5 h-5',
  },
  small: {
    root: 'h-8 px-1 py-1',
    icon: 'w-4.5 h-4.5',
  },
}

const disabled: ButtonDisabledVariants = {
  contained: {
    default: `disabled:bg-black-15 disabled:text-black-30`,
    inverse: `disabled:bg-white-15 disabled:text-white-30`,
  },
  outlined: {
    default: `disabled:bg-transparent disabled:border-black-15 disabled:text-black-30`,
    inverse: `disabled:bg-transparent disabled:border-white-30 disabled:text-white-30`,
  },
  text: {
    default: `disabled:bg-transparent disabled:text-black-30`,
    inverse: `disabled:bg-transparent disabled:text-white-30`,
  },
}

const positions: ButtonPositions = {
  contained: {
    default: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    inverse: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    primary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-primary-dark disabled:border-black-15`,
      right: 'rounded-r border-l border-primary-dark disabled:border-black-15',
    },
    secondary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-secondary-dark disabled:border-black-15`,
      right: `rounded-r border-l border-secondary-dark disabled:border-black-15`,
    },
    danger: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-error-darker disabled:border-black-15`,
      right: 'rounded-r border-l border-error-darker disabled:border-black-15',
    },
  },
  outlined: {
    default: {
      left: 'rounded-l disabled:border-black-20',
      middle: 'rounded-none border-l-0 disabled:border-black-20',
      right: 'rounded-r border-l-0 disabled:border-black-20',
    },
    inverse: {
      left: 'rounded-l disabled:border-white-100',
      middle: 'rounded-none border-l-0 disabled:border-white-100',
      right: 'rounded-r border-l-0 disabled:border-white-100',
    },
    primary: {
      left: 'rounded-l disabled:border-primary-main',
      middle: 'rounded-none border-l-0 disabled:border-primary-main',
      right: 'rounded-r border-l-0 disabled:border-primary-main',
    },
    secondary: {
      left: 'rounded-l disabled:border-secondary-main',
      middle: 'rounded-none border-l-0 disabled:border-secondary-main',
      right: 'rounded-r border-l-0 disabled:border-secondary-main',
    },
    danger: {
      left: 'rounded-l disabled:border-error-dark',
      middle: 'rounded-none border-l-0 disabled:border-error-dark',
      right: 'rounded-r border-l-0 disabled:border-error-dark',
    },
  },
  text: {
    default: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    inverse: {
      left: 'rounded-l',
      middle: 'rounded-none border-l border-black-15 disabled:border-black-15',
      right: 'rounded-r border-l border-black-15 disabled:border-black-15',
    },
    primary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-primary-dark disabled:border-black-15`,
      right: 'rounded-r border-l border-primary-dark disabled:border-black-15',
    },
    secondary: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-secondary-dark disabled:border-black-15`,
      right: `rounded-r border-l border-secondary-dark disabled:border-black-15`,
    },
    danger: {
      left: 'rounded-l',
      middle: `rounded-none border-l border-error-darker disabled:border-black-15`,
      right: 'rounded-r border-l border-error-darker disabled:border-black-15',
    },
  },
}

const variants: ButtonVariants = {
  contained: {
    default: `bg-black-5 text-black-90 ${disabled.contained.default} active:bg-black-25 focus:bg-black-15 focus-visible:ring hover:bg-black-15`,
    inverse: `bg-white-25 text-white-100 ${disabled.contained.inverse} active:bg-white-60 focus:bg-white-45 focus-visible:ring hover:bg-white-45`,
    primary: `bg-primary-main text-white-100 ${disabled.contained.default} active:bg-primary-darker focus:bg-primary-dark focus-visible:ring hover:bg-primary-dark`,
    secondary: `bg-secondary-main text-white-100 ${disabled.contained.default} active:bg-secondary-darker focus:bg-secondary-dark focus-visible:ring hover:bg-secondary-dark`,
    danger: `bg-error-main text-white-100 ${disabled.contained.default} active:bg-error-darker focus:bg-error-dark focus-visible:ring hover:bg-error-dark`,
  },
  outlined: {
    default: `border border-solid border-black-20 text-black-90 ${disabled.outlined.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
    inverse: `border border-solid border-white-100 text-white-100 ${disabled.outlined.inverse} active:bg-white-45 focus:bg-white-25 focus-visible:ring hover:bg-white-25`,
    primary: `border border-solid border-primary-main text-primary-main ${disabled.outlined.default} active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    secondary: `border border-solid border-secondary-main text-secondary-main ${disabled.outlined.default} active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    danger: `border border-solid border-error-darker text-error-darker ${disabled.outlined.default} active:bg-error-20 focus:bg-error-10 focus-visible:ring hover:bg-error-10`,
  },
  text: {
    default: `text-black-90 ${disabled.text.default} active:bg-black-15 focus:bg-black-5 focus-visible:ring hover:bg-black-5`,
    inverse: `text-white-100 ${disabled.text.inverse} active:bg-white-45 focus:bg-white-25 focus-visible:ring hover:bg-white-25`,
    primary: `text-primary-main ${disabled.text.default} active:bg-primary-20 focus:bg-primary-10 focus-visible:ring hover:bg-primary-10`,
    secondary: `text-secondary-main ${disabled.text.default} active:bg-secondary-20 focus:bg-secondary-10 focus-visible:ring hover:bg-secondary-10`,
    danger: `text-error-darker ${disabled.text.default} active:bg-error-20 focus:bg-error-10 focus-visible:ring hover:bg-error-10`,
  },
}

function ButtonComponent(
  {
    'aria-controls': controls,
    'aria-expanded': expanded,
    'aria-haspopup': hasPopup,
    children,
    color = 'default',
    dataSet,
    disabled = false,
    endAdornment: EndAdornment,
    form,
    href,
    id,
    onClick,
    onKeyDown,
    position = null,
    size = 'medium',
    startAdornment: StartAdornment,
    tabIndex = 0,
    target,
    type = 'button',
    variant = 'contained',
  }: ButtonProps,
  ref: Ref<HTMLAnchorElement> & Ref<HTMLButtonElement>,
): JSX.Element {
  const Component = href ? 'a' : 'button'

  return (
    <Component
      aria-controls={controls}
      aria-expanded={expanded}
      aria-haspopup={hasPopup}
      className={clsx(
        'font-sans text-sm font-medium leading-relaxed tracking-wide disabled:cursor-not-allowed focus:outline-none',
        variants[variant][color],
        sizes[size].root,
        position ? positions[variant][color][position] : 'rounded',
      )}
      {...toDataSet(dataSet)}
      disabled={disabled}
      form={form}
      href={href}
      id={id}
      onClick={onClick}
      onKeyDown={onKeyDown}
      ref={ref}
      tabIndex={tabIndex}
      {...(href ? { target } : { type })}
    >
      <div className="flex items-center">
        {StartAdornment ? (
          <span
            className={clsx(
              'flex items-center justify-center',
              sizes[size].icon,
            )}
          >
            <StartAdornment color="inherit" />
          </span>
        ) : null}
        {children?.type?.uiName === 'SvgIcon' ? (
          <span className={sizes[size].icon}>{children}</span>
        ) : (
          <span className={size === 'small' ? 'px-1' : 'px-2'}>{children}</span>
        )}
        {EndAdornment ? (
          <span
            className={clsx(
              'flex items-center justify-center',
              sizes[size].icon,
            )}
          >
            <EndAdornment color="inherit" />
          </span>
        ) : null}
      </div>
    </Component>
  )
}

const Button = forwardRef<HTMLAnchorElement | HTMLButtonElement, ButtonProps>(
  ButtonComponent,
)

export default Button
